import Image from 'next/image';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';

import { CheckIcon } from '~/components/icons';

import { SchoolLpProgram } from '../models/school-lp-program';

import { CareerPathStartingPoints } from './career-path-starting-points';

export type CareerPathRole = {
  id: string;
  tabLabel: string;
  name: string;
  /** Sanity CDN URL of the role pictogram; hidden while unset on the entry. */
  pictogramImage: string | null;
  description: string;
  skills: string[];
  programs: SchoolLpProgram[];
};

export type CareerPathPanelProps = {
  role: CareerPathRole;
  skillsTitle: string;
  startingPointsTitle: string;
  theme: {
    roleNameColor: string;
    bodyColor: string;
    asideBg: string;
    programCardBadgeImage: string;
  };
};

export function CareerPathPanel({ role, skillsTitle, startingPointsTitle, theme }: CareerPathPanelProps) {
  return (
    <Flex
      alignItems={{ lg: 'center' }}
      flexDirection={{ base: 'column', lg: 'row' }}
      gap='24px'
      justifyContent='space-between'
    >
      <Flex flex={1} flexDirection='column' gap='32px' justifyContent='space-between' padding={{ md: '24px' }}>
        <Flex flexDirection='column' gap='16px'>
          <Flex alignItems='center' gap='16px'>
            {role.pictogramImage && (
              <Box flexShrink={0} height='40px' position='relative' width='40px'>
                <Image alt='' height={40} src={role.pictogramImage} width={40} />
              </Box>
            )}
            <Heading as='h3' color={theme.roleNameColor} fontSize='1.5rem' letterSpacing='-0.01em' lineHeight='1.15'>
              {role.name}
            </Heading>
          </Flex>
          <Text color={theme.bodyColor} fontSize={{ base: '0.875rem', md: '1rem' }} lineHeight='1.5'>
            {role.description}
          </Text>
        </Flex>
        <Flex flexDirection='column' gap='16px'>
          <Flex alignItems='center' gap='12px'>
            <Text
              color={theme.roleNameColor}
              fontFamily='condensed'
              fontSize='0.9375rem'
              fontWeight='500'
              letterSpacing='0.9px'
              lineHeight='1'
              textTransform='uppercase'
              whiteSpace='nowrap'
            >
              {skillsTitle}
            </Text>
            <Box bg={theme.roleNameColor} flex={1} height='1px' opacity={0.4} />
          </Flex>
          <Flex as='ul' flexDirection='column' gap='16px' listStyleType='none'>
            {role.skills.map((skill) => (
              <Flex key={skill} alignItems='center' as='li' gap='10px'>
                <CheckIcon color={theme.roleNameColor} flexShrink={0} h='18px' w='18px' />
                <Text color={theme.bodyColor} fontSize={{ base: '0.875rem', md: '1rem' }} lineHeight='1.5'>
                  {skill}
                </Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Flex>
      <CareerPathStartingPoints
        badgeImage={theme.programCardBadgeImage}
        bg={theme.asideBg}
        programs={role.programs}
        title={startingPointsTitle}
      />
    </Flex>
  );
}
